/**
 * BuildingManager - Handles building placement, selection, and demolition
 */

import * as THREE from 'three';
import { World } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import { SceneManager } from '@/rendering/SceneManager';
import { TileHighlight } from '@/rendering/terrain/TileHighlight';
import { tileOccupancy } from '@/game/systems/TileOccupancy';
import { createBuildingEntity } from '@/game/entities/createBuilding';
import { wallConnectionSystem } from '@/game/systems/WallConnectionSystem';
import { EventBus } from '@/core/events/EventBus';
import { useGameStore } from '@/state/gameStore';
import { BUILDING_STATS } from '@/game/factions/FactionData';
import { getSurfaceY } from '@/rendering/terrain/TerrainGenerator';
import { BUILDING_NAMES } from './GameConstants';
import type { FactionId, BuildingType, Entity } from '@/types';
import type { PositionComponent } from '@/game/components/Position';
import type { FactionComponent } from '@/game/components/Faction';
import type { HealthComponent } from '@/game/components/Health';
import { type BuildingComponent, MAX_BUILDING_LEVEL, getUpgradeCost, getRepairCost } from '@/game/components/Building';
import { type WallComponent, createWallComponent } from '@/game/components/Wall';
import type { FactionManager } from '@/game/factions/FactionManager';
import type { EventLog } from '@/ui/components/EventLog';
import { audioManager } from '@/core/audio/AudioManager';

export class BuildingManager {
  private world: World;
  private sceneManager: SceneManager;
  private tileHighlight: TileHighlight;
  private factionManager: FactionManager;
  private eventLog: EventLog;

  private selectedBuildingType: BuildingType | null = null;
  private selectedBuildings: Set<Entity> = new Set();
  private buildingSelectionRings: THREE.Mesh[] = [];
  private ringGeometry: THREE.RingGeometry | null = null;
  private ringMaterial: THREE.MeshBasicMaterial | null = null;

  // Tower attack range indicator
  private readonly TOWER_RANGE = 6.0;
  private rangeCircle: THREE.Line | null = null;
  private rangeCircleMaterial: THREE.LineBasicMaterial | null = null;
  private selectionRangeCircles: THREE.Line[] = [];

  constructor(
    world: World,
    sceneManager: SceneManager,
    tileHighlight: TileHighlight,
    factionManager: FactionManager,
    eventLog: EventLog
  ) {
    this.world = world;
    this.sceneManager = sceneManager;
    this.tileHighlight = tileHighlight;
    this.factionManager = factionManager;
    this.eventLog = eventLog;
    this.createBuildingSelectionRing();
    this.createRangeCircle();
  }

  private createBuildingSelectionRing(): void {
    this.ringGeometry = new THREE.RingGeometry(0.8, 1.0, 32);
    this.ringGeometry.rotateX(-Math.PI / 2);

    this.ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xd4a84b,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });
  }

  private createRangeCircle(): void {
    // Create a circle geometry for tower attack range
    const segments = 64;
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new THREE.Vector3(
        Math.cos(angle) * this.TOWER_RANGE,
        0,
        Math.sin(angle) * this.TOWER_RANGE
      ));
    }
    const geometry = new THREE.BufferGeometry().setFromPoints(points);

    this.rangeCircleMaterial = new THREE.LineBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.6,
    });

    this.rangeCircle = new THREE.Line(geometry, this.rangeCircleMaterial);
    this.rangeCircle.visible = false;
    this.rangeCircle.renderOrder = 998;
    this.sceneManager.add(this.rangeCircle);
  }

  private getOrCreateSelectionRangeCircle(index: number): THREE.Line {
    if (index < this.selectionRangeCircles.length) {
      return this.selectionRangeCircles[index];
    }

    const segments = 64;
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      points.push(new THREE.Vector3(
        Math.cos(angle) * this.TOWER_RANGE,
        0,
        Math.sin(angle) * this.TOWER_RANGE
      ));
    }
    const geometry = new THREE.BufferGeometry().setFromPoints(points);

    const circle = new THREE.Line(geometry, this.rangeCircleMaterial!.clone());
    circle.visible = false;
    circle.renderOrder = 998;
    this.sceneManager.add(circle);
    this.selectionRangeCircles.push(circle);
    return circle;
  }

  private getOrCreateRing(index: number): THREE.Mesh {
    if (index < this.buildingSelectionRings.length) {
      return this.buildingSelectionRings[index];
    }

    const ring = new THREE.Mesh(this.ringGeometry!, this.ringMaterial!.clone());
    ring.visible = false;
    ring.renderOrder = 10;
    this.sceneManager.add(ring);
    this.buildingSelectionRings.push(ring);
    return ring;
  }

  setSelectedBuildingType(type: BuildingType | null): void {
    this.selectedBuildingType = type;
  }

  getSelectedBuildingType(): BuildingType | null {
    return this.selectedBuildingType;
  }

  hasSelectedBuildings(): boolean {
    return this.selectedBuildings.size > 0;
  }

  hasSelectedBuildingType(): boolean {
    return this.selectedBuildingType !== null;
  }

  cancelBuildingPlacement(): void {
    this.selectedBuildingType = null;
    this.tileHighlight.clearHighlight();
    useGameStore.getState().setSelectedBuildingType(null);
    if (this.rangeCircle) {
      this.rangeCircle.visible = false;
    }
  }

  getBuildingAtPosition(worldPos: { x: number; z: number }, playerFaction: FactionId): Entity | null {
    const tileX = Math.floor(worldPos.x);
    const tileZ = Math.floor(worldPos.z);

    const occupiedInfo = tileOccupancy.getOccupyingEntity(tileX, tileZ);
    if (!occupiedInfo) return null;

    const faction = this.world.getComponent<FactionComponent>(occupiedInfo.entityId, ComponentTypes.Faction);
    if (!faction || faction.factionId !== playerFaction) return null;

    return occupiedInfo.entityId;
  }

  selectBuilding(entity: Entity, addToSelection: boolean = false): void {
    if (addToSelection) {
      if (this.selectedBuildings.has(entity)) {
        this.selectedBuildings.delete(entity);
      } else {
        this.selectedBuildings.add(entity);
      }
    } else {
      this.selectedBuildings.clear();
      this.selectedBuildings.add(entity);
    }

    if (this.selectedBuildings.size === 0) {
      useGameStore.getState().setSelectedBuilding(null, null);
      useGameStore.getState().setSelectedEntities([]);
    } else if (this.selectedBuildings.size === 1) {
      const building = this.world.getComponent<BuildingComponent>(entity, ComponentTypes.Building);
      const buildingName = building ? BUILDING_NAMES[building.buildingType] || building.buildingType : '建筑';
      useGameStore.getState().setSelectedBuilding(entity, buildingName);
      useGameStore.getState().setSelectedEntities([entity]);
    } else {
      useGameStore.getState().setSelectedBuilding(-1, `已选中 ${this.selectedBuildings.size} 个建筑`);
      useGameStore.getState().setSelectedEntities([...this.selectedBuildings]);
    }
  }

  deselectBuilding(): void {
    this.selectedBuildings.clear();
    useGameStore.getState().setSelectedBuilding(null, null);
    useGameStore.getState().setSelectedEntities([]);
    for (const ring of this.buildingSelectionRings) {
      ring.visible = false;
    }
  }

  demolishSelectedBuildings(): void {
    if (this.selectedBuildings.size === 0) return;

    let totalRefund = 0;
    let demolishedCount = 0;
    const toRemove: Entity[] = [];

    for (const entity of this.selectedBuildings) {
      const building = this.world.getComponent<BuildingComponent>(entity, ComponentTypes.Building);
      const faction = this.world.getComponent<FactionComponent>(entity, ComponentTypes.Faction);

      if (!building || !faction) continue;

      if (building.buildingType === 'capital') {
        this.eventLog.addEvent('拆除失败', '主城不可拆除', 'info');
        continue;
      }

      const stats = BUILDING_STATS[building.buildingType];
      const refund = Math.floor(stats.cost * 0.5);
      totalRefund += refund;
      this.factionManager.addGold(faction.factionId, refund);

      tileOccupancy.releaseTiles(entity);

      EventBus.emit('building:destroyed', {
        entityId: entity,
        factionId: faction.factionId,
      });

      this.world.destroyEntity(entity);
      toRemove.push(entity);
      demolishedCount++;
    }

    for (const entity of toRemove) {
      this.selectedBuildings.delete(entity);
    }

    if (demolishedCount > 0) {
      if (demolishedCount === 1) {
        this.eventLog.addEvent('建筑拆除', `建筑已拆除，返还${totalRefund}金币`, 'info');
      } else {
        this.eventLog.addEvent('建筑拆除', `${demolishedCount}个建筑已拆除，返还${totalRefund}金币`, 'info');
      }
    }

    this.deselectBuilding();
  }

  placeBuildingAt(worldPos: { x: number; z: number }, playerFaction: FactionId): boolean {
    if (!this.selectedBuildingType) return false;

    const placementResult = tileOccupancy.canPlaceBuilding(worldPos.x, worldPos.z, this.selectedBuildingType);
    if (!placementResult.valid) {
      if (placementResult.reason === 'tile_occupied') {
        this.eventLog.addEvent('建造失败', '地块已被占用', 'info');
      } else if (placementResult.reason === 'not_land') {
        this.eventLog.addEvent('建造失败', '无法在水上建造', 'info');
      } else if (placementResult.reason === 'not_in_territory') {
        this.eventLog.addEvent('建造失败', '只能在己方领土内建造', 'info');
      } else {
        this.eventLog.addEvent('建造失败', '地形不允许建造', 'info');
      }
      return false;
    }

    const stats = BUILDING_STATS[this.selectedBuildingType];
    if (!this.factionManager.spendGold(playerFaction, stats.cost)) {
      this.eventLog.addEvent('建造失败', '金币不足', 'info');
      return false;
    }

    const tileX = Math.floor(worldPos.x) + 0.5;
    const tileZ = Math.floor(worldPos.z) + 0.5;
    const tileCenterX = Math.floor(worldPos.x);
    const tileCenterZ = Math.floor(worldPos.z);
    const surfaceY = getSurfaceY(tileX, tileZ);
    const entity = createBuildingEntity(this.world, this.selectedBuildingType, playerFaction, tileX, surfaceY, tileZ);

    tileOccupancy.occupyTiles(tileX, tileZ, this.selectedBuildingType, entity);

    if (this.selectedBuildingType === 'wall') {
      const wallComponent = createWallComponent(tileCenterX, tileCenterZ);
      this.world.addComponent(entity, ComponentTypes.Wall, wallComponent);
      wallConnectionSystem.registerWall(entity, tileCenterX, tileCenterZ);

      EventBus.emit('wall:placed', {
        entityId: entity,
        factionId: playerFaction,
        tileX: tileCenterX,
        tileZ: tileCenterZ,
      });
    }

    EventBus.emit('building:placed', {
      entityId: entity,
      factionId: playerFaction,
      buildingType: this.selectedBuildingType,
      x: tileX,
      z: tileZ,
    });

    return true;
  }

  updateBuildingHighlight(pointerX: number, pointerY: number, getWorldPosition: (x: number, y: number) => { x: number; z: number }): void {
    if (!this.selectedBuildingType) {
      if (this.tileHighlight.isVisible()) {
        this.tileHighlight.clearHighlight();
      }
      // Hide range circle when not placing
      if (this.rangeCircle) {
        this.rangeCircle.visible = false;
      }
      return;
    }

    const worldPos = getWorldPosition(pointerX, pointerY);
    const placementResult = tileOccupancy.canPlaceBuilding(worldPos.x, worldPos.z, this.selectedBuildingType);
    const buildingSize = tileOccupancy.getBuildingSize(this.selectedBuildingType);

    this.tileHighlight.updateHighlight(worldPos.x, worldPos.z, buildingSize, placementResult.valid);

    // Show range circle for tower placement
    if (this.rangeCircle) {
      if (this.selectedBuildingType === 'tower') {
        const tileX = Math.floor(worldPos.x) + 0.5;
        const tileZ = Math.floor(worldPos.z) + 0.5;
        const surfaceY = getSurfaceY(tileX, tileZ);
        this.rangeCircle.position.set(tileX, surfaceY + 0.1, tileZ);
        this.rangeCircle.visible = true;
        // Change color based on placement validity
        (this.rangeCircle.material as THREE.LineBasicMaterial).color.setHex(
          placementResult.valid ? 0xff6600 : 0xff3333
        );
      } else {
        this.rangeCircle.visible = false;
      }
    }
  }

  upgradeBuilding(entityId: Entity): void {
    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const building = this.world.getComponent<BuildingComponent>(entityId, ComponentTypes.Building);
    const faction = this.world.getComponent<FactionComponent>(entityId, ComponentTypes.Faction);

    if (!building || !faction) return;
    if (faction.factionId !== playerFaction) return;

    if (building.level >= MAX_BUILDING_LEVEL) {
      this.eventLog.addEvent('升级失败', '建筑已达最高等级', 'info');
      return;
    }

    const stats = BUILDING_STATS[building.buildingType];
    const cost = getUpgradeCost(stats.cost, building.level);

    if (!this.factionManager.spendGold(playerFaction, cost)) {
      this.eventLog.addEvent('升级失败', '金币不足', 'info');
      return;
    }

    building.level++;

    this.eventLog.addEvent('升级完成', `${BUILDING_NAMES[building.buildingType]} 升级至 Lv.${building.level}`, 'build');
    audioManager.playSFX('build_complete');

    const selectedEntities = [...useGameStore.getState().selectedEntities];
    useGameStore.getState().setSelectedEntities([]);
    setTimeout(() => {
      useGameStore.getState().setSelectedEntities(selectedEntities);
    }, 50);
  }

  repairBuilding(entityId: Entity): void {
    const playerFaction = useGameStore.getState().playerFaction;
    if (!playerFaction) return;

    const building = this.world.getComponent<BuildingComponent>(entityId, ComponentTypes.Building);
    const faction = this.world.getComponent<FactionComponent>(entityId, ComponentTypes.Faction);
    const health = this.world.getComponent<HealthComponent>(entityId, ComponentTypes.Health);

    if (!building || !faction || !health) return;
    if (faction.factionId !== playerFaction) return;

    const damage = health.max - health.current;
    if (damage <= 0) {
      this.eventLog.addEvent('修复失败', '建筑无需修复', 'info');
      return;
    }

    const cost = getRepairCost(damage);

    if (!this.factionManager.spendGold(playerFaction, cost)) {
      this.eventLog.addEvent('修复失败', '金币不足', 'info');
      return;
    }

    health.current = health.max;

    this.eventLog.addEvent('修复完成', `${BUILDING_NAMES[building.buildingType]} 已修复`, 'build');
    audioManager.playSFX('build_complete');

    const selectedEntities = [...useGameStore.getState().selectedEntities];
    useGameStore.getState().setSelectedEntities([]);
    setTimeout(() => {
      useGameStore.getState().setSelectedEntities(selectedEntities);
    }, 50);
  }

  updateSelectionRings(): void {
    for (const ring of this.buildingSelectionRings) {
      ring.visible = false;
    }
    for (const circle of this.selectionRangeCircles) {
      circle.visible = false;
    }

    if (this.selectedBuildings.size === 0) return;

    const time = performance.now() * 0.003;
    const pulse = 0.6 + Math.sin(time) * 0.2;

    let ringIndex = 0;
    let rangeCircleIndex = 0;
    for (const entity of this.selectedBuildings) {
      const position = this.world.getComponent<PositionComponent>(entity, ComponentTypes.Position);
      const building = this.world.getComponent<BuildingComponent>(entity, ComponentTypes.Building);

      if (!position || !building) continue;

      const ring = this.getOrCreateRing(ringIndex);

      const stats = BUILDING_STATS[building.buildingType];
      const scale = Math.max(stats.size.width, stats.size.depth) * 0.55;

      ring.position.set(position.x, position.y + 0.15, position.z);
      ring.scale.set(scale, scale, scale);
      ring.visible = true;
      (ring.material as THREE.MeshBasicMaterial).opacity = pulse;

      ringIndex++;

      // Show attack range circle for towers
      if (building.buildingType === 'tower') {
        const rangeCircle = this.getOrCreateSelectionRangeCircle(rangeCircleIndex);
        rangeCircle.position.set(position.x, position.y + 0.1, position.z);
        rangeCircle.visible = true;
        (rangeCircle.material as THREE.LineBasicMaterial).opacity = pulse * 0.8;
        rangeCircleIndex++;
      }
    }
  }

  clear(): void {
    this.selectedBuildings.clear();
    this.selectedBuildingType = null;
    useGameStore.getState().setSelectedBuilding(null, null);
    for (const ring of this.buildingSelectionRings) {
      ring.visible = false;
    }
    for (const circle of this.selectionRangeCircles) {
      circle.visible = false;
    }
    if (this.rangeCircle) {
      this.rangeCircle.visible = false;
    }
  }

  dispose(): void {
    this.clear();
    this.ringGeometry?.dispose();
    this.ringMaterial?.dispose();
    for (const ring of this.buildingSelectionRings) {
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
      this.sceneManager.scene.remove(ring);
    }
    this.buildingSelectionRings = [];

    // Dispose range circles
    if (this.rangeCircle) {
      this.rangeCircle.geometry.dispose();
      (this.rangeCircle.material as THREE.Material).dispose();
      this.sceneManager.scene.remove(this.rangeCircle);
      this.rangeCircle = null;
    }
    this.rangeCircleMaterial?.dispose();
    for (const circle of this.selectionRangeCircles) {
      circle.geometry.dispose();
      (circle.material as THREE.Material).dispose();
      this.sceneManager.scene.remove(circle);
    }
    this.selectionRangeCircles = [];
  }
}
